import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search, Plus, Edit, Trash2, Eye, EyeOff, Copy, RefreshCw, BarChart3,
  CheckSquare, Square, Filter, X,
} from 'lucide-react'
import { contentApi, type ContentAudience, type ContentCategory, type ContentListItem } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useContentBasePath, MEDICAL_AUTHOR_BASE } from '../../lib/contentRoutes'

const mediaLabel: Record<string, { label: string; color: string }> = {
  text: { label: '图文', color: 'bg-blue-100 text-blue-700' },
  video: { label: '视频', color: 'bg-purple-100 text-purple-700' },
  audio: { label: '音频', color: 'bg-orange-100 text-orange-700' },
}

/** 投稿来源角色展示名（author_role 落库值 → 中文） */
const authorRoleLabel: Record<string, string> = {
  admin: '总后台',
  supervisor: '机构负责人',
  staff: '医护人员',
  mcp: 'AI 生成',
  user: '普通用户',
}

type FilterState = {
  keyword: string
  status: '' | 'draft' | 'pending_review' | 'published' | 'offline' | 'rejected'
  media_type: '' | 'text' | 'video' | 'audio'
  parent_category_id: '' | string
  category_id: '' | string
  audience_id: '' | string
  date_field: 'created_at' | 'published_at' | 'updated_at'
  date_from: string
  date_to: string
}

const blankFilter: FilterState = {
  keyword: '',
  status: '',
  media_type: '',
  parent_category_id: '',
  category_id: '',
  audience_id: '',
  date_field: 'created_at',
  date_from: '',
  date_to: '',
}

const ContentList = () => {
  const nav = useNavigate()
  const { user } = useAuth()
  // 列表页在两个入口下复用（管理端 /admin/content、医护端 /medical/author），
  // 跳转前缀必须跟随入口，否则医护端会被甩回管理端布局
  const basePath = useContentBasePath()
  // 医护端入口不做「本机构投稿」范围切换（见下方注释）
  const isMedicalEntry = basePath === MEDICAL_AUTHOR_BASE
  // 本页面同时服务三方（内容投稿权限开放后）：
  //   admin      → 全量内容管理，可发布/下线/批量操作
  //   supervisor → 我的投稿 + 本机构投稿（只读下属稿件），不能发布或审核
  //   staff      → 仅我的投稿，只能改自己的草稿/被驳回稿件
  const isAdmin = user?.role === 'admin'
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
  const [scope, setScope] = useState<'mine' | 'hospital'>('mine')
  const [searchParams, setSearchParams] = useSearchParams()

  const [items, setItems] = useState<ContentListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filter, setFilter] = useState<FilterState>(() => {
    const f = { ...blankFilter }
    for (const k of Object.keys(blankFilter) as (keyof FilterState)[]) {
      const v = searchParams.get(k)
      if (v) (f as any)[k] = v
    }
    return f
  })
  const [showFilters, setShowFilters] = useState(false)

  const [categories, setCategories] = useState<ContentCategory[]>([])
  const [audiences, setAudiences] = useState<ContentAudience[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [batchBusy, setBatchBusy] = useState(false)

  const level1 = useMemo(() => categories.filter(c => c.level === 1), [categories])
  const level2ByParent = useMemo(() => {
    const m = new Map<string, ContentCategory[]>()
    for (const c of categories.filter(c => c.level === 2)) {
      if (!c.parent_id) continue
      if (!m.has(c.parent_id)) m.set(c.parent_id, [])
      m.get(c.parent_id)!.push(c)
    }
    return m
  }, [categories])
  const level2Options = filter.parent_category_id ? (level2ByParent.get(filter.parent_category_id) || []) : []

  const load = async (f: FilterState = filter, p = page) => {
    setLoading(true)
    setError('')
    try {
      const common = {
        keyword: f.keyword || undefined,
        status: f.status || undefined,
        media_type: f.media_type || undefined,
        parent_category_id: f.parent_category_id || undefined,
        category_id: f.category_id || undefined,
        audience_id: f.audience_id || undefined,
        date_field: f.date_field,
        date_from: f.date_from || undefined,
        date_to: f.date_to || undefined,
        page: p,
        page_size: pageSize,
      }
      // 管理端走 admin 接口（全量），医护走 author 接口（服务端强制作用域过滤）
      const res = isAdmin
        ? await contentApi.adminListContents(common)
        : await contentApi.authorListContents({
            ...common,
            scope: scope === 'hospital' ? 'hospital' : undefined,
          })
      setItems(res.items)
      setTotal(res.total)
      setSelected(new Set())
    } catch (e: any) {
      setError(e.message || '加载失败')
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // 管理端与作者端的字典接口不同：作者端只返回启用分类，且非投稿人会被拒
    const catFn = isAdmin ? contentApi.adminListCategoriesAll : contentApi.authorCategories
    const audFn = isAdmin ? contentApi.adminAudiences : contentApi.authorAudiences
    catFn().then(setCategories).catch(() => setCategories([]))
    audFn().then(setAudiences).catch(() => setAudiences([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  useEffect(() => {
    load(filter, page)
    // 同步 URL，便于刷新和分享
    const sp = new URLSearchParams()
    Object.entries(filter).forEach(([k, v]) => { if (v) sp.set(k, String(v)) })
    if (page !== 1) sp.set('page', String(page))
    setSearchParams(sp, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, page, pageSize, scope])

  const setFilterField = <K extends keyof FilterState>(k: K, v: FilterState[K]) => {
    setPage(1)
    if (k === 'parent_category_id') {
      // 一级变化时清空二级
      setFilter(prev => ({ ...prev, parent_category_id: v as string, category_id: '' }))
    } else if (k === 'category_id') {
      setFilter(prev => ({ ...prev, category_id: v as string }))
    } else {
      setFilter(prev => ({ ...prev, [k]: v }))
    }
  }

  const onResetFilter = () => {
    setFilter(blankFilter)
    setPage(1)
  }

  const onPublish = async (id: string) => {
    if (!confirm('确认发布？发布后将在用户端展示。')) return
    try {
      await contentApi.adminPublishContent(id)
      load()
    } catch (e: any) { alert(e.message || '发布失败') }
  }
  const onOffline = async (id: string) => {
    if (!confirm('确认下线？')) return
    try {
      await contentApi.adminOfflineContent(id)
      load()
    } catch (e: any) { alert(e.message || '下线失败') }
  }
  const onDelete = async (id: string) => {
    const msg = isAdmin
      ? '确认删除？发布后内容请先下线。'
      : '确认删除该稿件？删除后不可恢复。'
    if (!confirm(msg)) return
    try {
      if (isAdmin) await contentApi.adminDeleteContent(id)
      else await contentApi.authorDeleteContent(id)
      load()
    } catch (e: any) { alert(e.message || '删除失败') }
  }
  // 投稿人专属操作：提交审核 / 撤回
  const onSubmitReview = async (id: string) => {
    if (!confirm('确认提交审核？提交后在审核完成前不能编辑。')) return
    try {
      await contentApi.authorSubmitReview(id)
      load()
    } catch (e: any) { alert(e.message || '提交失败') }
  }
  const onWithdraw = async (id: string) => {
    if (!confirm('确认撤回？撤回后稿件回到草稿状态，可继续编辑。')) return
    try {
      await contentApi.authorWithdrawReview(id)
      load()
    } catch (e: any) { alert(e.message || '撤回失败') }
  }
  const onDuplicate = (item: ContentListItem) => {
    // 跳到新建页并预填（通过 location state），或直接编辑并改 title
    const draftTitle = `${item.title} - 副本`
    if (confirm(`将基于「${item.title}」创建草稿副本，继续？`)) {
      nav(basePath + '/new', {
        state: {
          duplicate: true,
          source_id: item.id,
          title: draftTitle,
          category_id: item.category_id,
          audience_id: item.audience_id,
          media_type: item.media_type,
          author: item.author,
          summary: item.summary,
        }
      })
    }
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const allOnPageSelected = items.length > 0 && items.every(i => selected.has(i.id))
  const toggleAll = () => {
    setSelected(prev => {
      if (allOnPageSelected) return new Set()
      return new Set(items.map(i => i.id))
    })
  }
  const batchAction = async (action: 'publish' | 'offline' | 'delete') => {
    const ids = Array.from(selected)
    if (ids.length === 0) return alert('请先勾选内容')
    const labelMap = { publish: '发布', offline: '下线', delete: '删除' }
    if (!confirm(`确认批量${labelMap[action]} ${ids.length} 条内容？`)) return
    setBatchBusy(true)
    try {
      const fn = action === 'publish' ? contentApi.adminBatchPublish
        : action === 'offline' ? contentApi.adminBatchOffline
        : contentApi.adminBatchDelete
      const res = await fn(ids)
      setBatchBusy(false)
      const failedItems = (res.items || []).filter((i: any) => !i.ok)
      if (failedItems.length === 0) {
        alert(`成功${res.succeeded} 条`)
      } else {
        const msg = failedItems.slice(0, 10).map((i: any) => `  ${i.id.slice(0, 8)}: ${i.error || '失败'}`).join('\n')
        const more = failedItems.length > 10 ? '\n  ... 共 ' + failedItems.length + ' 条失败' : ''
        alert(`${res.succeeded} 成功，${res.failed} 失败：\n${msg}${more}`)
      }
      load()
    } catch (e: any) {
      setBatchBusy(false)
      alert(`批量操作失败：${e.message || '网络错误'}`)
    }
  }


  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const activeFilterCount = Object.entries(filter).filter(([k, v]) => v && k !== 'date_field').length
  // 作者列对总后台与机构负责人有意义（机构负责人需要看清投稿人是谁）
  const showAuthorCol = isAdmin || isSupervisor
  const colCount = 8 + (isAdmin ? 1 : 0) + (showAuthorCol ? 1 : 0)

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{isAdmin ? '内容管理' : '我的投稿'}</h1>
          <p className="text-sm text-gray-500 mt-1">
            共 <span className="text-green-700 font-semibold">{total}</span> 条
            {activeFilterCount > 0 && <span className="ml-2">· 已应用 {activeFilterCount} 项筛选</span>}
            {!isAdmin && <span className="ml-2">· 提交后由总后台审核，通过后在用户端展示</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Link to="/admin/content/statistics" className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded hover:bg-gray-50">
              <BarChart3 className="w-4 h-4" /> 统计
            </Link>
          )}
          <Link to={basePath + '/new'} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
            <Plus className="w-4 h-4" /> {isAdmin ? '新建内容' : '投稿撰写'}
          </Link>
        </div>
      </div>

      {/* 机构负责人：我的投稿 / 本机构投稿（查看下属稿件，只读）
          医护端（/medical/author）不做范围切换 —— 业务确认「医护端只看自己的投稿」，
          机构维度的汇总仍由总后台查看，避免同一个入口出现两套口径。 */}
      {isSupervisor && !isMedicalEntry && (
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => { setScope('mine'); setPage(1) }}
            className={`px-3 py-1.5 rounded border ${scope === 'mine' ? 'bg-green-600 text-white border-green-600' : 'bg-white hover:bg-gray-50'}`}
          >
            我的投稿
          </button>
          <button
            onClick={() => { setScope('hospital'); setPage(1) }}
            className={`px-3 py-1.5 rounded border ${scope === 'hospital' ? 'bg-green-600 text-white border-green-600' : 'bg-white hover:bg-gray-50'}`}
          >
            本机构投稿
          </button>
        </div>
      )}

      {/* Search row (always visible) */}
      <div className="bg-white rounded border p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filter.keyword}
            onChange={e => setFilterField('keyword', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load() }}
            placeholder="搜索标题 / 摘要"
            className="w-full border rounded pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`inline-flex items-center gap-1 px-3 py-2 text-sm border rounded ${showFilters || activeFilterCount > 0 ? 'bg-green-50 border-green-300 text-green-700' : 'hover:bg-gray-50'}`}
        >
          <Filter className="w-4 h-4" />
          筛选{activeFilterCount > 0 && <span className="ml-1 px-1 bg-green-600 text-white text-xs rounded-full">{countOfActiveFilters(filter)}</span>}
        </button>
        {activeFilterCount > 0 && (
          <button onClick={onResetFilter} className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
            <X className="w-4 h-4" /> 清空
          </button>
        )}
        <button onClick={() => load()} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200">
          <RefreshCw className="w-4 h-4" /> 刷新
        </button>
      </div>

      {/* Advanced filters */}
      {showFilters && (
        <div className="bg-white rounded border p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <select value={filter.status} onChange={e => setFilterField('status', e.target.value as FilterState['status'])} className="border rounded px-3 py-2 text-sm">
            <option value="">全部状态</option>
            <option value="draft">草稿</option>
            <option value="pending_review">待审核</option>
            <option value="published">已发布</option>
            <option value="offline">已下线</option>
            <option value="rejected">已驳回</option>
          </select>
          <select value={filter.media_type} onChange={e => setFilterField('media_type', e.target.value as FilterState['media_type'])} className="border rounded px-3 py-2 text-sm">
            <option value="">全部媒体</option>
            <option value="text">图文</option>
            <option value="video">视频</option>
            <option value="audio">音频</option>
          </select>
          <select value={filter.parent_category_id} onChange={e => setFilterField('parent_category_id', e.target.value)} className="border rounded px-3 py-2 text-sm">
            <option value="">全部分类（一级）</option>
            {level1.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={filter.category_id}
            onChange={e => setFilterField('category_id', e.target.value)}
            disabled={!filter.parent_category_id}
            className="border rounded px-3 py-2 text-sm disabled:bg-gray-100"
          >
            <option value="">全部分类（二级）</option>
            {level2Options.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filter.audience_id} onChange={e => setFilterField('audience_id', e.target.value)} className="border rounded px-3 py-2 text-sm">
            <option value="">全部人群</option>
            {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={filter.date_field} onChange={e => setFilterField('date_field', e.target.value as FilterState['date_field'])} className="border rounded px-3 py-2 text-sm">
            <option value="created_at">按创建时间</option>
            <option value="published_at">按发布时间</option>
            <option value="updated_at">按更新时间</option>
          </select>
          <input type="date" value={filter.date_from} onChange={e => setFilterField('date_from', e.target.value)} className="border rounded px-3 py-2 text-sm" placeholder="开始" />
          <input type="date" value={filter.date_to} onChange={e => setFilterField('date_to', e.target.value)} className="border rounded px-3 py-2 text-sm" placeholder="结束" />
        </div>
      )}

      {/* Batch action bar：批量发布/下线/删除仅总后台可用 */}
      {isAdmin && selected.size > 0 && (
        <div className="bg-green-50 border border-green-200 rounded px-3 py-2 flex items-center gap-2 text-sm">
          <span className="text-green-700">已选 <b>{selected.size}</b> 条</span>
          <div className="flex-1" />
          <button disabled={batchBusy} onClick={() => batchAction('publish')} className="px-3 py-1 text-green-700 hover:bg-green-100 rounded disabled:opacity-50">批量发布</button>
          <button disabled={batchBusy} onClick={() => batchAction('offline')} className="px-3 py-1 text-yellow-700 hover:bg-yellow-100 rounded disabled:opacity-50">批量下线</button>
          <button disabled={batchBusy} onClick={() => batchAction('delete')} className="px-3 py-1 text-red-700 hover:bg-red-100 rounded disabled:opacity-50">批量删除</button>
          <button disabled={batchBusy} onClick={() => setSelected(new Set())} className="px-3 py-1 text-gray-500 hover:bg-gray-100 rounded">取消选择</button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm flex items-center gap-2">
          <span>{error}</span>
          <button onClick={() => load()} className="ml-auto underline">重试</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {isAdmin && (
                <th className="px-2 py-2 w-8">
                  <button onClick={toggleAll} className="text-gray-500 hover:text-gray-700">
                    {allOnPageSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
              )}
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">标题</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">分类</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">媒体</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">人群</th>
              {showAuthorCol && <th className="px-3 py-2 text-left font-medium whitespace-nowrap">作者 / 机构</th>}
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">状态</th>
              <th className="px-3 py-2 text-right font-medium whitespace-nowrap">阅读量</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">发布时间</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colCount} className="text-center py-10 text-gray-400">
                <div className="inline-flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" />加载中…</div>
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={colCount} className="text-center py-10 text-gray-400">
                {activeFilterCount > 0 ? '当前筛选条件下无内容' : (isAdmin ? '暂无内容，去新建第一条吧' : '还没有投稿，去写第一篇吧')}
              </td></tr>
            )}
            {!loading && items.map(c => {
              const cfg = mediaLabel[c.media_type] || mediaLabel.text
              const checked = selected.has(c.id)
              // 归属判断：author_account_id 为空的历史数据视为可操作（与后端兜底口径一致）
              const mine = !c.author_account_id || c.author_account_id === user?.userId
              const editable = isAdmin || (mine && (c.status === 'draft' || c.status === 'rejected'))
              const deletable = isAdmin || (mine && (c.status === 'draft' || c.status === 'rejected'))
              return (
                <tr key={c.id} className={`border-t hover:bg-gray-50 ${checked ? 'bg-green-50/50' : ''}`}>
                  {isAdmin && (
                    <td className="px-2 py-2">
                      <button onClick={() => toggleSelect(c.id)} className="text-gray-500 hover:text-gray-700">
                        {checked ? <CheckSquare className="w-4 h-4 text-green-600" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="font-medium line-clamp-1">{c.title}</div>
                    {c.summary && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{c.summary}</div>}
                    {/* 驳回原因是作者最需要看到的信息，直接贴在标题下方 */}
                    {!isAdmin && c.status === 'rejected' && c.review_note && (
                      <div className="text-xs text-red-600 mt-1">驳回原因：{c.review_note}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {c.parent_category_name && <span className="text-gray-400">{c.parent_category_name} / </span>}
                    {c.category_name || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.audience_name || '-'}</td>
                  {showAuthorCol && (
                    <td className="px-3 py-2 text-xs text-gray-600">
                      <div className="max-w-[120px] truncate" title={c.author_name || c.author || ''}>
                        {c.author_name || c.author || '-'}
                      </div>
                      {(c.author_hospital_name || c.author_role) && (
                        <div
                          className="text-gray-400 mt-0.5 max-w-[160px] truncate"
                          title={`${c.author_hospital_name || ''}${c.author_role ? ' · ' + (authorRoleLabel[c.author_role] || c.author_role) : ''}`}
                        >
                          {c.author_hospital_name || '-'}
                          {c.author_role ? ` · ${authorRoleLabel[c.author_role] || c.author_role}` : ''}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2"><StatusTag status={c.status} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.view_count}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{(c.published_at || c.created_at)?.slice?.(0, 16)?.replace?.('T', ' ') || '-'}</td>
                  <td className="px-3 py-2 space-x-2 text-xs whitespace-nowrap">
                    <Link className="text-blue-600 hover:underline" to={`${basePath}/${c.id}`}>
                      {editable ? <><Edit className="w-3 h-3 inline" /> 编辑</> : <><Eye className="w-3 h-3 inline" /> 查看</>}
                    </Link>
                    {c.status === 'published' && (
                      <Link className="text-purple-600 hover:underline" to={`/user/knowledge/${c.id}`} target="_blank"><Eye className="w-3 h-3 inline" /> 预览</Link>
                    )}
                    {/* 总后台：复制 + 发布 / 下线 */}
                    {isAdmin && (
                      <>
                        <button onClick={() => onDuplicate(c)} className="text-gray-600 hover:underline"><Copy className="w-3 h-3 inline" /> 复制</button>
                        {(c.status === 'draft' || c.status === 'offline') && (
                          <button onClick={() => onPublish(c.id)} className="text-green-600 hover:underline">
                            {c.status === 'offline' ? '重新发布' : '发布'}
                          </button>
                        )}
                        {c.status === 'published' && (
                          <button onClick={() => onOffline(c.id)} className="text-yellow-600 hover:underline"><EyeOff className="w-3 h-3 inline" /> 下线</button>
                        )}
                      </>
                    )}
                    {/* 投稿人：提交审核 / 撤回（仅限自己的稿件） */}
                    {!isAdmin && mine && (c.status === 'draft' || c.status === 'rejected') && (
                      <button onClick={() => onSubmitReview(c.id)} className="text-green-600 hover:underline">提交审核</button>
                    )}
                    {!isAdmin && mine && c.status === 'pending_review' && (
                      <button onClick={() => onWithdraw(c.id)} className="text-amber-600 hover:underline">撤回</button>
                    )}
                    {deletable && (
                      <button onClick={() => onDelete(c.id)} className="text-red-600 hover:underline"><Trash2 className="w-3 h-3 inline" /> 删除</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm flex-wrap gap-2">
        <div className="flex items-center gap-2 text-gray-500">
          <span>每页</span>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1) }} className="border rounded px-2 py-1 text-sm">
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>条 · 第 {page} / {totalPages} 页</span>
        </div>
        <div className="flex items-center gap-1">
          <button disabled={page <= 1} onClick={() => setPage(1)} className="px-2 py-1 border rounded disabled:opacity-40">首页</button>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">上一页</button>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">下一页</button>
          <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="px-2 py-1 border rounded disabled:opacity-40">末页</button>
        </div>
      </div>
    </div>
  )
}

function countOfActiveFilters(f: FilterState): number {
  let n = 0
  if (f.keyword) n++
  if (f.status) n++
  if (f.media_type) n++
  if (f.parent_category_id) n++
  if (f.category_id) n++
  if (f.audience_id) n++
  if (f.date_from) n++
  if (f.date_to) n++
  return n
}

const StatusTag = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    pending_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-teal-100 text-teal-700',
    published: 'bg-green-100 text-green-700',
    offline: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
  }
  const label: Record<string, string> = {
    draft: '草稿',
    pending_review: '待审核',
    approved: '已通过',
    published: '已发布',
    offline: '已下线',
    rejected: '已驳回',
  }
  return <span className={`inline-block text-xs px-2 py-0.5 rounded whitespace-nowrap ${map[status] || 'bg-gray-100 text-gray-700'}`}>{label[status] || status}</span>
}

export default ContentList